import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { DashboardLayout } from '../components/DashboardLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { reportsAPI } from '../lib/api';
import { usePortfolioPrefsStore } from '../store';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { BROSDashboardPnlTab } from './BROSDashboardPnlTab';
import { BROSDashboardCashflowTab } from './BROSDashboardCashflowTab';
import { BROSDashboardSalesTab } from './BROSDashboardSalesTab';
import { BROSRecordsList } from '../components/bros/BROSRecordsList';

// ── Period options ────────────────────────────────────────────────────

const PERIODS = [
  { value: 'ytd', label: 'YTD' },
  { value: 'last_12m', label: 'Last 12M' },
  { value: 'all', label: 'All Time' },
];

// ── Sub-tab definitions per main tab ─────────────────────────────────

const PNL_SUB_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'invoices', label: 'Invoices' },
];

// Bookings by closing date live under Sales (Fan, 2026-10-28); the P&L
// Bookings list is by invoice due date like the chart.
const SALES_SUB_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'bookings', label: 'Bookings' },
];

const CASHFLOW_SUB_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'invoices', label: 'Invoices' },
];

// Sub-tab bar (underline style). Props destructured in body (babel-metadata-plugin workaround).
function SubTabBar(props) {
  const { tabs, active, onChange } = props;
  if (!tabs || tabs.length < 2) return null;
  return (
    <div className="flex gap-1 border-b mb-6">
      {tabs.map((st) => (
        <button
          key={st.value}
          onClick={() => onChange(st.value)}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
            active === st.value
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
          )}
        >
          {st.label}
        </button>
      ))}
    </div>
  );
}

// ── Main page (shell with tabs) ──────────────────────────────────────

export function BROSDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'pnl';
  const sub = searchParams.get('sub') || 'overview';

  const [period, setPeriod] = useState('ytd');
  const { displayCurrency, setDisplayCurrency } = usePortfolioPrefsStore();

  // P&L data
  const [pnlData, setPnlData] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(true);
  const [pnlRefreshKey, setPnlRefreshKey] = useState(0);

  // Sales by closing date (lazy-loaded, Fan 2026-10-24)
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);

  // Cashflow data (lazy-loaded)
  const [cashflowData, setCashflowData] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(false);

  const handleTabChange = useCallback((value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'pnl') {
        next.delete('tab');
      } else {
        next.set('tab', value);
      }
      // Reset sub-tab when switching main tabs
      next.delete('sub');
      return next;
    });
  }, [setSearchParams]);

  const handleSubChange = useCallback((value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'overview') {
        next.delete('sub');
      } else {
        next.set('sub', value);
      }
      return next;
    });
  }, [setSearchParams]);

  // ── P&L fetch (always on mount + period change) ──
  const fetchPnl = useCallback(async (p) => {
    setPnlLoading(true);
    try {
      const res = await reportsAPI.brosDashboard(p);
      setPnlData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPnlLoading(false);
    }
  }, []);

  // ── Cashflow fetch (lazy) ──
  const fetchCashflow = useCallback(async (p) => {
    setCashflowLoading(true);
    try {
      const res = await reportsAPI.brosCashflow(p);
      setCashflowData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCashflowLoading(false);
    }
  }, []);

  // ── Sales fetch (lazy) ──
  const fetchSales = useCallback(async (p) => {
    setSalesLoading(true);
    try {
      const res = await reportsAPI.brosSales(p);
      setSalesData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSalesLoading(false);
    }
  }, []);

  // Always fetch P&L on mount / period change; invalidate cashflow cache
  useEffect(() => {
    fetchPnl(period);
    setCashflowData(null);
    setSalesData(null);
  }, [period, fetchPnl]);

  // Lazy fetch cashflow when tab activated
  useEffect(() => {
    if (tab === 'cashflow' && !cashflowData && !cashflowLoading) {
      fetchCashflow(period);
    }
  }, [tab, cashflowData, cashflowLoading, period, fetchCashflow]);

  useEffect(() => {
    if (tab === 'sales' && !salesData && !salesLoading) {
      fetchSales(period);
    }
  }, [tab, salesData, salesLoading, period, fetchSales]);

  // Sales close date per booking (HubSpot close dates included), so the
  // Sales > Bookings list groups exactly like the Sales tab.
  const salesClosedOn = useMemo(() => {
    const out = {};
    for (const m of salesData?.months || []) {
      for (const r of m.rows || []) {
        if (r.booking_id && r.closed_on && r.kind !== 'cancellation') out[r.booking_id] = r.closed_on;
      }
    }
    return out;
  }, [salesData]);

  // ── Currency conversion (shared across tabs) ──
  const gbpToEur = useMemo(() => {
    const eurGbp = pnlData?.fx_rates?.EUR_GBP;
    return eurGbp && eurGbp > 0 ? 1 / eurGbp : null;
  }, [pnlData]);

  const dc = displayCurrency;
  const cx = useCallback(
    (v) => (dc === 'EUR' && gbpToEur) ? v * gbpToEur : v,
    [dc, gbpToEur]
  );

  // Refresh handler for the active tab
  const handleRefresh = useCallback(() => {
    if (tab === 'cashflow') {
      fetchCashflow(period);
    } else if (tab === 'sales') {
      fetchSales(period);
    } else {
      fetchPnl(period);
      setPnlRefreshKey((k) => k + 1);
      setCashflowData(null);
    }
  }, [tab, period, fetchPnl, fetchCashflow, fetchSales]);

  const isLoading = tab === 'cashflow' ? cashflowLoading : tab === 'sales' ? salesLoading : pnlLoading;

  // Initial full-page loader (P&L not yet loaded)
  if (pnlLoading && !pnlData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* ── Header + period + currency + refresh ── */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">BR OS Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              {pnlData?.period_label || 'Bonjour Residences operating overview'}
              {pnlData?.date_range && (
                <span className="ml-2">
                  ({pnlData.date_range.start} to {pnlData.date_range.end})
                </span>
              )}
              {' \u00B7 '}{dc}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded transition-all',
                    period === p.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {/* Currency toggle */}
            <CurrencyToggle />
            <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="pnl">P&L</TabsTrigger>
            <TabsTrigger value="cashflow">Cashflow</TabsTrigger>
            <TabsTrigger value="sales">Sales</TabsTrigger>
          </TabsList>
          <TabsContent value="pnl" className="mt-6">
            <SubTabBar tabs={PNL_SUB_TABS} active={sub} onChange={handleSubChange} />

            {sub === 'bookings' ? (
              <BROSRecordsList type="bookings" displayCurrency={dc} fxRates={pnlData?.fx_rates} />
            ) : sub === 'invoices' ? (
              <BROSRecordsList type="invoices" displayCurrency={dc} fxRates={pnlData?.fx_rates} />
            ) : (
              <BROSDashboardPnlTab data={pnlData} loading={pnlLoading} dc={dc} cx={cx} period={period} refreshKey={pnlRefreshKey} />
            )}
          </TabsContent>
          <TabsContent value="cashflow" className="mt-6">
            <SubTabBar tabs={CASHFLOW_SUB_TABS} active={sub} onChange={handleSubChange} />

            {sub === 'invoices' ? (
              <BROSRecordsList type="invoices" showPaymentStatus displayCurrency={dc} fxRates={pnlData?.fx_rates} />
            ) : cashflowLoading && !cashflowData ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <BROSDashboardCashflowTab data={cashflowData} loading={cashflowLoading} dc={dc} cx={cx} period={period} />
            )}
          </TabsContent>
          <TabsContent value="sales" className="mt-6">
            <SubTabBar tabs={SALES_SUB_TABS} active={sub} onChange={handleSubChange} />

            {sub === 'bookings' ? (
              <BROSRecordsList type="bookings" basis="closing" closedOn={salesClosedOn} displayCurrency={dc} fxRates={pnlData?.fx_rates} />
            ) : salesLoading && !salesData ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <BROSDashboardSalesTab data={salesData} loading={salesLoading} dc={dc} cx={cx} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
